/*
  Rebuy Event Manager
  
  Centralizes all event handling for the Rebuy Smart Cart:
  - Provides a pub/sub system for cart events
  - Handles cart state changes and discount code applications
  - Dispatches events to registered handlers
  - Maintains a single source of truth for cart state
*/

window.RebuyEventManager = (function () {
  // Private variables
  const eventHandlers = {
    'cart:change': [],
    'cart:ready': [],
    'cart:show': [],
    'discount:applied': [],
    'discount:removed': [],
  };

  // Current cart state
  let currentCart = null;
  let currentDiscount = 0;
  let isSmartCartOpen = false;
  let consentmoLauncherHiddenByTheme = false;

  // Private methods
  async function fetchLatestCart() {
    try {
      currentCart = await window.RebuyUtils.fetchCart();
      currentDiscount = window.RebuyUtils.getActiveDiscount();
      return currentCart;
    } catch (error) {
      console.error('[RebuyEventManager] Error fetching cart:', error);
      return null;
    }
  }

  function getConsentmoHost() {
    return document.querySelector('csm-cookie-consent');
  }

  function isMobileViewport() {
    return window.innerWidth <= 699;
  }

  function isConsentmoPopoverOpen(consentmoHost) {
    if (!consentmoHost) return false;

    try {
      return consentmoHost.matches(':popover-open');
    } catch (error) {
      return false;
    }
  }

  function hideConsentmoLauncher() {
    const consentmoHost = document.querySelector('csm-cookie-consent');
    if (!consentmoHost) return;

    if (typeof consentmoHost.hidePopover === 'function' && isConsentmoPopoverOpen(consentmoHost)) {
      consentmoHost.hidePopover();
      consentmoLauncherHiddenByTheme = true;
      return;
    }

    if (!consentmoLauncherHiddenByTheme) {
      consentmoHost.dataset.rebuyConsentmoDisplay = consentmoHost.style.display || '';
    }

    consentmoHost.style.setProperty('display', 'none', 'important');
    consentmoLauncherHiddenByTheme = true;
  }

  function showConsentmoLauncher() {
    const consentmoHost = getConsentmoHost();
    if (!consentmoHost || !consentmoLauncherHiddenByTheme) return;

    if (consentmoHost.dataset.rebuyConsentmoDisplay) {
      consentmoHost.style.setProperty('display', consentmoHost.dataset.rebuyConsentmoDisplay);
    } else {
      consentmoHost.style.removeProperty('display');
    }

    delete consentmoHost.dataset.rebuyConsentmoDisplay;

    if (typeof consentmoHost.showPopover === 'function' && !isConsentmoPopoverOpen(consentmoHost)) {
      try {
        consentmoHost.showPopover();
      } catch (error) {
        // No-op: fallback display restoration above is enough if Popover API fails.
      }
    }

    consentmoLauncherHiddenByTheme = false;
  }

  function syncConsentmoLauncherState() {
    if (isSmartCartOpen && isMobileViewport()) {
      hideConsentmoLauncher();
      return;
    }

    showConsentmoLauncher();
  }

  function triggerEvent(eventName, data) {
    if (!eventHandlers[eventName] || eventHandlers[eventName].length === 0) {
      return;
    }

    eventHandlers[eventName].forEach(handler => {
      try {
        handler(data);
      } catch (error) {
        console.error(`[RebuyEventManager] Error in handler for ${eventName}:`, error);
      }
    });
  }

  // Initialize event listeners
  function initEventListeners() {
    window.addEventListener('resize', syncConsentmoLauncherState);

    // Listen for cart change events from Rebuy
    document.addEventListener('rebuy:cart.change', async () => {
      const cart = await fetchLatestCart();
      if (cart) {
        triggerEvent('cart:change', { cart, discount: currentDiscount });
      }
    });

    // Listen for cart ready
    document.addEventListener('rebuy:smartcart.ready', async () => {
      syncConsentmoLauncherState();
      const cart = await fetchLatestCart();
      if (cart) {
        triggerEvent('cart:ready', { cart, discount: currentDiscount });
      }
    });

    // Listen for cart drawer open
    document.addEventListener('rebuy:smartcart.show', async () => {
      isSmartCartOpen = true;
      syncConsentmoLauncherState();
      const cart = await fetchLatestCart();
      if (cart) {
        triggerEvent('cart:show', { cart, discount: currentDiscount });
      }
    });

    document.addEventListener('rebuy:smartcart.hide', () => {
      isSmartCartOpen = false;
      syncConsentmoLauncherState();
    });

    // Listen for discount code application
    document.addEventListener('click', async e => {
      // Apply discount button clicked
      const applyButton = e.target.closest('[data-rebuy-component="discount-input"] .rebuy-button');
      if (applyButton) {
        // Wait for discount to be applied
        await new Promise(resolve => setTimeout(resolve, 2000));
        const cart = await fetchLatestCart();
        if (cart) {
          triggerEvent('discount:applied', {
            cart,
            discount: currentDiscount,
          });
        }
      }

      // Remove discount button clicked
      const removeDiscountButton = e.target.closest('.rebuy-cart__discount-tag-remove');
      if (removeDiscountButton) {
        // Wait for discount to be removed
        await new Promise(resolve => setTimeout(resolve, 1000));
        const cart = await fetchLatestCart();
        if (cart) {
          triggerEvent('discount:removed', {
            cart,
            discount: currentDiscount,
          });
        }
      }
    });
  }

  // Public API
  return {
    // Initialize the event manager
    init: function () {
      syncConsentmoLauncherState();
      initEventListeners();
      return this;
    },

    // Register event handlers
    on: function (eventName, handler) {
      if (!eventHandlers[eventName]) {
        eventHandlers[eventName] = [];
      }
      eventHandlers[eventName].push(handler);
      return this;
    },

    // Remove event handler
    off: function (eventName, handler) {
      if (!eventHandlers[eventName]) return this;

      const index = eventHandlers[eventName].indexOf(handler);
      if (index !== -1) {
        eventHandlers[eventName].splice(index, 1);
      }
      return this;
    },

    // Get current cart state
    getCart: function () {
      return currentCart;
    },

    // Get current discount
    getDiscount: function () {
      return currentDiscount;
    },

    // Force refresh cart data
    refreshCart: async function () {
      const cart = await fetchLatestCart();
      if (cart) {
        triggerEvent('cart:change', { cart, discount: currentDiscount });
      }
      return cart;
    },
  };
})();
